import { useMutation, useQuery } from "@tanstack/react-query";

import type { SupportTicketDetail } from "../../shared/support-schemas";
import { FieldError } from "../components/ui/field";
import type { OperationsNavigate } from "./navigation";
import { OperationsLink } from "./OperationsLink";
import {
  LIVE_LIST,
  listSupportTickets,
  listSupportTools,
  supportKeys,
  updateSupportTicket,
} from "./support-api";
import { LabeledSelect } from "./support-fields";
import { formatWhen, requesterLabel, STATUS_LABELS } from "./support-format";
import { supportTicketPath } from "./support-route";

const linkClass = "font-semibold text-link hover:underline";

/**
 * What we know around the ticket: who asked, which customer and tool it concerns, who else is on
 * it, and the customer's other open tickets (ADR-0089).
 */
export function SupportTicketContext({
  ticket,
  navigate,
  canWrite,
  onUpdated,
}: {
  ticket: SupportTicketDetail;
  navigate: OperationsNavigate;
  canWrite: boolean;
  onUpdated: (ticket: SupportTicketDetail) => void;
}) {
  const customerPath = `/customers/${encodeURIComponent(ticket.customerSlug)}`;
  return (
    <aside
      aria-label="Customer context"
      className="flex flex-col gap-5 rounded-xl bg-card px-5 py-5 shadow-card"
    >
      <section aria-label="Requester" className="flex flex-col gap-1">
        <h2 className="text-sm font-semibold text-muted-foreground">Requester</h2>
        <p className="font-semibold">{requesterLabel(ticket.requester)}</p>
        <p className="text-sm break-all text-muted-foreground">
          {ticket.requester.email ?? "No email address on file"}
        </p>
      </section>
      <section aria-label="Customer" className="flex flex-col gap-1">
        <h2 className="text-sm font-semibold text-muted-foreground">Customer</h2>
        <OperationsLink href={customerPath} navigate={navigate} className={linkClass}>
          {ticket.customerName}
        </OperationsLink>
        <TicketTool
          ticket={ticket}
          customerPath={customerPath}
          navigate={navigate}
          canWrite={canWrite}
          onUpdated={onUpdated}
        />
        {ticket.deployedVersion ? (
          <p className="text-sm text-muted-foreground">Version {ticket.deployedVersion}</p>
        ) : null}
      </section>
      {ticket.participants.length > 1 ? (
        <section aria-label="People on this ticket" className="flex flex-col gap-1">
          <h2 className="text-sm font-semibold text-muted-foreground">People on this ticket</h2>
          <ul className="flex flex-col gap-0.5 text-sm">
            {ticket.participants.map((person) => (
              <li key={person.email} className="break-all">
                {person.name ? `${person.name} <${person.email}>` : person.email}
                {person.role === "cc" ? " (copied)" : ""}
              </li>
            ))}
          </ul>
        </section>
      ) : null}
      <OtherTickets ticket={ticket} navigate={navigate} />
    </aside>
  );
}

/** "No tool", the customer's tools, and a linked tool the list no longer shows. */
function toolOptions(known: readonly { id: string; name: string }[], current: string | null) {
  const listed = known.some((tool) => tool.id === current);
  return [
    { value: "", label: "No tool" },
    ...(current !== null && !listed
      ? [{ value: current, label: "A tool that is no longer listed" }]
      : []),
    ...known.map((tool) => ({ value: tool.id, label: tool.name })),
  ];
}

/**
 * The tool the ticket is about. Anyone who works the ticket can link one of the customer's tools,
 * change it, or unlink it; the link opens the tool's hub.
 */
function TicketTool({
  ticket,
  customerPath,
  navigate,
  canWrite,
  onUpdated,
}: {
  ticket: SupportTicketDetail;
  customerPath: string;
  navigate: OperationsNavigate;
  canWrite: boolean;
  onUpdated: (ticket: SupportTicketDetail) => void;
}) {
  const tools = useQuery({
    queryKey: supportKeys.tools(ticket.organizationId),
    queryFn: () => listSupportTools(ticket.organizationId),
  });
  const save = useMutation({
    mutationFn: (toolId: string | null) => updateSupportTicket(ticket.id, { toolId }),
    onSuccess: (result) => {
      onUpdated(result.ticket);
    },
  });
  const known = tools.data?.tools ?? [];
  const current = ticket.toolId;
  const linked = known.find((tool) => tool.id === current);
  const options = toolOptions(known, current);
  return (
    <div className="mt-2 flex flex-col gap-1">
      {canWrite ? (
        <LabeledSelect
          id="support-tool"
          label="Tool"
          value={current ?? ""}
          options={options}
          disabled={save.isPending || tools.isPending}
          onChange={(value) => {
            save.mutate(value === "" ? null : value);
          }}
        />
      ) : (
        <p className="text-sm">{linked?.name ?? (current ? "A linked tool" : "No tool linked")}</p>
      )}
      {current ? (
        <OperationsLink
          href={`${customerPath}/tools/${encodeURIComponent(current)}`}
          navigate={navigate}
          className={linkClass}
        >
          Open the tool hub
        </OperationsLink>
      ) : null}
      {save.error ? <FieldError>{save.error.message}</FieldError> : null}
    </div>
  );
}

function OtherTickets({
  ticket,
  navigate,
}: {
  ticket: SupportTicketDetail;
  navigate: OperationsNavigate;
}) {
  const others = useQuery({
    queryKey: supportKeys.list("open", "", ticket.organizationId),
    queryFn: () =>
      listSupportTickets({ view: "open", organizationId: ticket.organizationId, limit: 6 }),
    ...LIVE_LIST,
  });
  const rows = (others.data?.tickets ?? []).filter((other) => other.id !== ticket.id).slice(0, 5);
  return (
    <section aria-label="Other open tickets" className="flex flex-col gap-2">
      <h2 className="text-sm font-semibold text-muted-foreground">Other open tickets</h2>
      {others.isSuccess && rows.length === 0 ? (
        <p className="text-sm text-muted-foreground">None</p>
      ) : null}
      <ul className="flex flex-col gap-2">
        {rows.map((other) => (
          <li key={other.id} className="flex flex-col">
            <OperationsLink
              href={supportTicketPath(other.id)}
              navigate={navigate}
              className={linkClass}
            >
              {other.subject}
            </OperationsLink>
            <span className="text-sm text-muted-foreground">
              {other.reference} · {STATUS_LABELS[other.status]} · {formatWhen(other.updatedAt)}
            </span>
          </li>
        ))}
      </ul>
    </section>
  );
}
